import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { readWeightInfo } from '../utils/weight';
import { useWebAuth } from './WebAuthContext';
import { useToast } from './ToastContext';
import { CartSyncClient, cartReference, cartKey, GUEST_ENVELOPE_KEY, operationId, readGuestCart } from '../services/cartSync';

const PreinvoiceContext = createContext(null);

const STORAGE_KEY = 'preinvoice_cart_v1';

function safeParse(json) {
  try { return JSON.parse(json); } catch { return null; }
}

function normalizeBrand(product) {
  const brands = Array.isArray(product?.brands) ? product.brands.filter(Boolean) : [];

  const selectedBrandId =
    product?.selected_brand_id ??
    product?.brand_id ??
    product?.brandId ??
    (brands.length === 1 ? brands[0]?.id : null);

  const selectedBrand = brands.find((b) => String(b.id) === String(selectedBrandId));

  const selectedBrandName =
    product?.selected_brand_name ||
    product?.brand_name ||
    product?.brandName ||
    selectedBrand?.name ||
    selectedBrand?.title ||
    (brands.length === 1 ? (brands[0]?.name || brands[0]?.title) : null);

  return {
    brand_id: selectedBrandId ? Number(selectedBrandId) : null,
    brand_name: selectedBrandName || null,
  };
}


function readWeightInfoLegacy(product) {
  const sources = [product, product?.tech_data, product?.features, product?.specs].filter(Boolean);
  const keys = ['weight', 'وزن', 'وزن واحد', 'وزن هر شاخه', 'weight_kg', 'unit_weight'];
  for (const source of sources) {
    if (typeof source !== 'object') continue;
    for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return { weight_value: String(value), weight_label: key };
      }
    }
    for (const [key, value] of Object.entries(source)) {
      if (String(key).toLowerCase().includes('weight') || String(key).includes('وزن')) {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          return { weight_value: String(value), weight_label: key };
        }
      }
    }
  }
  return { weight_value: null, weight_label: null };
}

function makeCartId(product) {
  const id =
    product?.cart_id ||
    product?.id ||
    product?.product_item_id ||
    (product?.category_id ? `category-${product.category_id}` : '');
  const { brand_id } = normalizeBrand(product);
  return `${id}:${brand_id || 'no-brand'}`;
}

export function PreinvoiceProvider({ children }) {
  const { token, user, ready, isAuthed, logout } = useWebAuth();
  const { showToast } = useToast();
  const accountId = ready && isAuthed ? String(user.id) : null;
  const [guestItems, setGuestItems] = useState(() => readGuestCart().items);
  const [synced, setSynced] = useState({ owner: null, items: [], ready: false, status: 'loading', revision: 0 });
  const [drafts, setDrafts] = useState({ owner: null, values: {} });
  const clientRef = useRef(null);
  const rawItems = !ready ? [] : accountId ? (synced.owner === accountId ? synced.items : []) : guestItems;
  const items = accountId && drafts.owner === accountId ? rawItems.map((item) => Object.hasOwn(drafts.values, item.cart_id) ? { ...item, quantity: drafts.values[item.cart_id] } : item) : rawItems;
  const [lastAdded, setLastAdded] = useState(null);

  useEffect(() => {
    try {
      const previous = readGuestCart();
      const changed = JSON.stringify(previous.items) !== JSON.stringify(guestItems);
      localStorage.setItem(GUEST_ENVELOPE_KEY, JSON.stringify({ id: changed ? operationId() : previous.id, items: guestItems }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(guestItems));
    } catch { /* The guest cart remains usable when browser storage is disabled. */ }
  }, [guestItems]);

  useEffect(() => {
    const update = (event) => { if (event.key === GUEST_ENVELOPE_KEY) setGuestItems(readGuestCart().items); };
    window.addEventListener('storage', update);
    return () => window.removeEventListener('storage', update);
  }, []);

  useEffect(() => {
    setDrafts({ owner: accountId, values: {} });
    setLastAdded(null);
    if (!accountId) { clientRef.current = null; return undefined; }
    const client = new CartSyncClient({
      userId: accountId, token,
      onChange: setSynced,
      onError: (message) => showToast({ title: 'وضعیت سبد', message }),
      onExpired: () => { showToast({ title: 'ورود دوباره لازم است', message: 'نشست شما پایان یافته است.' }); logout(); },
      onMerge: (merged) => setGuestItems((previous) => previous.flatMap((item) => {
        const key = cartKey(cartReference(item));
        const consumed = merged.filter((row) => cartKey(cartReference(row)) === key).reduce((sum, row) => sum + Number(row.quantity || 1), 0);
        const quantity = Number(item.quantity || 1) - consumed;
        return quantity > 0 ? [{ ...item, quantity }] : [];
      })),
    });
    clientRef.current = client;
    client.start();
    const logoutListener = () => {
      if (client.pending.length) showToast({ title: 'سبد ذخیره‌نشده', message: 'تغییرات آفلاین ذخیره نشده‌اند؛ سبد سروری حساب شما حفظ شده است.' });
      client.stop();
      client.pending = client.pending.map(({ preview, previews, ...operation }) => operation);
      client.persist();
    };
    window.addEventListener('web-auth-logout', logoutListener);
    return () => { window.removeEventListener('web-auth-logout', logoutListener); client.stop(); if (clientRef.current === client) clientRef.current = null; };
  }, [accountId, token]);

  function addItem(product, quantity = 1) {
    const rawId =
      product?.id ||
      product?.product_item_id ||
      (product?.category_id ? `category-${product.category_id}` : '');
    if (!rawId) return;
    const numericProductId = Number(product?.product_item_id || product?.id);
    const hasRealProductId = Number.isFinite(numericProductId) && numericProductId > 0 && !product?.is_catalog_category;

    const qty = Math.max(Number(quantity) || 1, 1);
    const brand = normalizeBrand(product);
    const cart_id = makeCartId(product);
    const productCode = product.product_code || product.sku || product.external_code || product.barcode || null;
    const title = product.title || product.name || product.sku || 'محصول';
    const weightInfo = readWeightInfo(product);

    if (accountId) {
      const accepted = clientRef.current?.enqueue('add', { ...product, id: hasRealProductId ? numericProductId : rawId, product_item_id: hasRealProductId ? numericProductId : null, is_catalog_category: !hasRealProductId, title, ...brand, ...weightInfo, quantity: qty, sku: productCode, product_code: productCode });
      if (accepted) setLastAdded({ cart_id, title, quantity: qty, at: Date.now() });
      return;
    }
    setGuestItems((prev) => {
      const idx = prev.findIndex((x) => (x.cart_id || makeCartId(x)) === cart_id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          ...brand,
          weight_value: weightInfo.weight_value ?? next[idx].weight_value ?? null,
          weight_label: weightInfo.weight_label ?? next[idx].weight_label ?? null,
          weight_unit: weightInfo.weight_unit || next[idx].weight_unit || null,
          quantity: (Number(next[idx].quantity) || 0) + qty,
        };
        return next;
      }

      return [
        ...prev,
        {
          cart_id,
          id: hasRealProductId ? numericProductId : rawId,
          product_item_id: hasRealProductId ? numericProductId : null,
          category_id: product.category_id || null,
          is_catalog_category: Boolean(product?.is_catalog_category),
          sku: productCode,
          product_code: productCode,
          title,
          ...brand,
          unit: product.unit || null,
          price: product.price ?? product.unit_price ?? null,
          image_url:
            product.image_url ||
            product.image ||
            product.thumbnail_url ||
            product.thumbnail ||
            product.hero_image_url ||
            product.category_image_url ||
            product.category_hero_image_url ||
            null,
          weight_value: weightInfo.weight_value,
          weight_label: weightInfo.weight_label,
          weight_unit: weightInfo.weight_unit,
          quantity: qty,
        },
      ];
    });
    setLastAdded({ cart_id, title, quantity: qty, at: Date.now() });
  }

  function setQty(key, quantity) {
    if (accountId) {
      const item = clientRef.current?.items().find((row) => (row.cart_id || row.id) === key);
      if (!item) return;
      if (quantity === '') { setDrafts((previous) => ({ owner: accountId, values: { ...previous.values, [key]: '' } })); return; }
      setDrafts((previous) => { const values = { ...previous.values }; delete values[key]; return { owner: accountId, values }; });
      const next = Math.max(Number(quantity) || 1, 1);
      if (next !== Number(item.quantity)) clientRef.current.enqueue('set', { ...item, quantity: next }, item.quantity);
      return;
    }
    if (quantity === '') {
      setGuestItems((prev) => prev.map((x) => ((x.cart_id || x.id) === key ? { ...x, quantity: '' } : x)));
      return;
    }
    const qty = Math.max(Number(quantity) || 1, 1);
    setGuestItems((prev) => prev.map((x) => ((x.cart_id || x.id) === key ? { ...x, quantity: qty } : x)));
  }

  function removeItem(key) {
    if (accountId) {
      const item = clientRef.current?.items().find((row) => (row.cart_id || row.id) === key);
      if (item) clientRef.current.enqueue('remove', item, item.quantity);
    } else setGuestItems((prev) => prev.filter((x) => (x.cart_id || x.id) !== key));
  }

  function clear() {
    if (accountId) clientRef.current?.enqueue('clear');
    else setGuestItems([]);
  }

  async function flushCart() {
    if (!accountId || !clientRef.current) throw new Error('سبد حساب هنوز آماده نیست.');
    if (drafts.owner === accountId && Object.keys(drafts.values).length) throw new Error('تعداد کالا را کامل وارد کنید.');
    return clientRef.current.flush();
  }
  function acceptCart(snapshot) { clientRef.current?.accept(snapshot); }
  const syncStatus = accountId ? (synced.owner === accountId ? synced.status : 'loading') : 'local';

  const totalCount = useMemo(
    () => items.reduce((sum, x) => sum + (Number(x.quantity) || 0), 0),
    [items]
  );

  const value = useMemo(
    () => ({ items, addItem, setQty, removeItem, clear, totalCount, lastAdded, syncStatus, flushCart, acceptCart }),
    [items, totalCount, lastAdded, syncStatus, accountId, drafts]
  );
  return <PreinvoiceContext.Provider value={value}>{children}</PreinvoiceContext.Provider>;
}

export function usePreinvoice() {
  const ctx = useContext(PreinvoiceContext);
  if (!ctx) throw new Error('usePreinvoice must be used within PreinvoiceProvider');
  return ctx;
}
